import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import LinearProgress from "@mui/material/LinearProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { formatNumber } from "utils/format";

const STAT_COLORS = { atk: "error", def: "info", hp: "success", mp: "warning" };

/** Ô chỉ số tổng, hiện to ở đầu bảng kết quả. */
function TotalTile({ label, value, color }) {
  return (
    <Grid item xs={6} md={3}>
      <MDBox
        p={2}
        borderRadius="lg"
        textAlign="center"
        variant="gradient"
        bgColor={color}
        coloredShadow={color}
      >
        <MDTypography variant="caption" color="white" opacity={0.85} display="block">
          {label}
        </MDTypography>
        <MDTypography variant="h4" color="white" fontWeight="bold">
          {formatNumber(value)}
        </MDTypography>
      </MDBox>
    </Grid>
  );
}

TotalTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
};

function Cell({ children, align, bold, color }) {
  return (
    <MDBox
      component="td"
      py={0.75}
      px={1.5}
      textAlign={align}
      sx={({ palette, borders }) => ({
        borderBottom: `${borders.borderWidth[1]} solid ${palette.light.main}`,
      })}
    >
      <MDTypography
        variant="caption"
        color={color}
        fontWeight={bold ? "bold" : "regular"}
        sx={{ whiteSpace: "nowrap" }}
      >
        {children}
      </MDTypography>
    </MDBox>
  );
}

Cell.defaultProps = { align: "left", bold: false, color: "dark" };

Cell.propTypes = {
  children: PropTypes.node,
  align: PropTypes.string,
  bold: PropTypes.bool,
  color: PropTypes.string,
};

/** "41%" của một nguồn trong tổng, để thấy ngay nguồn nào đang gánh build. */
function share(value, total) {
  if (!total || value <= 0) return "";
  return `${Math.round((value / total) * 100)}%`;
}

function BreakdownTable({ rows, total }) {
  return (
    <MDBox sx={{ overflowX: "auto" }}>
      <Table size="small">
        <MDBox component="thead">
          <TableRow>
            <Cell bold color="secondary">
              Nguồn
            </Cell>
            <Cell bold align="right" color="secondary">
              Công
            </Cell>
            <Cell bold align="right" color="secondary">
              Thủ
            </Cell>
            <Cell bold align="right" color="secondary">
              HP
            </Cell>
            <Cell bold align="right" color="secondary">
              MP
            </Cell>
          </TableRow>
        </MDBox>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <Cell>{row.label}</Cell>
              {["atk", "def", "hp", "mp"].map((key) => (
                <Cell key={key} align="right">
                  {row[key] === 0 ? "—" : `${formatNumber(row[key])}`}
                  {row[key] > 0 && (
                    <MDTypography variant="caption" color="text" sx={{ ml: 0.5 }}>
                      {share(row[key], total[key])}
                    </MDTypography>
                  )}
                </Cell>
              ))}
            </TableRow>
          ))}
          <TableRow>
            <Cell bold>Tổng</Cell>
            <Cell bold align="right">
              {formatNumber(total.atk)}
            </Cell>
            <Cell bold align="right">
              {formatNumber(total.def)}
            </Cell>
            <Cell bold align="right">
              {formatNumber(total.hp)}
            </Cell>
            <Cell bold align="right">
              {formatNumber(total.mp)}
            </Cell>
          </TableRow>
        </TableBody>
      </Table>
    </MDBox>
  );
}

BreakdownTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  total: PropTypes.object.isRequired,
};

function CombatRow({ label, value, hint }) {
  return (
    <Grid item xs={6} md={4}>
      <MDTypography variant="caption" color="text" display="block">
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="bold" display="block">
        {value}
      </MDTypography>
      {hint && (
        <MDTypography variant="caption" color="text" display="block">
          {hint}
        </MDTypography>
      )}
    </Grid>
  );
}

CombatRow.defaultProps = { hint: null };

CombatRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  hint: PropTypes.string,
};

/**
 * Kết quả do máy chủ tính. Giao diện ở đây KHÔNG tính lại gì — mọi con số, kể cả % giảm thủ và
 * tỉ lệ chí mạng, đều là của `Pet` thật sau khi gọi `ApplyStats`.
 */
function ResultCard({ result, loading, error }) {
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!result) {
    return (
      <MDTypography variant="button" color="text">
        Chọn một mẫu pet để bắt đầu.
      </MDTypography>
    );
  }

  const { total, breakdown, combat, points, gemSets, hiddenStats, warnings, power } = result;

  return (
    <MDBox>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        <TotalTile label="Công" value={total.atk} color={STAT_COLORS.atk} />
        <TotalTile label="Thủ" value={total.def} color={STAT_COLORS.def} />
        <TotalTile label="HP" value={total.hp} color={STAT_COLORS.hp} />
        <TotalTile label="MP" value={total.mp} color={STAT_COLORS.mp} />
      </Grid>

      <MDBox display="flex" gap={0.5} flexWrap="wrap" mt={2}>
        {/* Chip chỉ nhận màu palette mà MUI có augment contrastText (primary, secondary,
            error, warning, info, success, default). Màu riêng của Material Dashboard như
            "dark" chỉ có main/focus nên làm alpha() nổ — dùng cho MDBox thì được. */}
        <Chip
          size="small"
          color="secondary"
          label={`Điểm sức mạnh (PvP): ${formatNumber(power)}`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`Sức ${formatNumber(result.pet.str)} / Nhanh ${formatNumber(
            result.pet.agi
          )} / Trí ${formatNumber(result.pet._int)}`}
        />
        <Chip
          size="small"
          variant="outlined"
          color={points.left < 0 ? "error" : "default"}
          label={`Tiềm năng ${formatNumber(points.used)}/${formatNumber(points.total)}`}
        />
      </MDBox>

      {warnings.length > 0 && (
        <MDBox mt={2}>
          {warnings.map((text) => (
            <Alert severity="warning" key={text} sx={{ mb: 1 }}>
              {text}
            </Alert>
          ))}
        </MDBox>
      )}

      <Divider sx={{ my: 2.5 }} />

      <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
        Chỉ số đến từ đâu
      </MDTypography>
      <BreakdownTable rows={breakdown} total={total} />
      <MDTypography variant="caption" color="text" display="block" mt={1}>
        Thứ tự đúng như trong game: hai dòng % của cánh và cải trang nhân vào tổng đang có nên nằm
        cuối. Dòng &quot;còn lại&quot; là phần đó cộng với mọi nguồn chưa phân loại — tổng của bảng
        luôn khớp chỉ số thật.
      </MDTypography>

      <Divider sx={{ my: 2.5 }} />

      <MDTypography variant="button" fontWeight="bold" display="block" mb={1.5}>
        Chỉ số phái sinh trong trận
      </MDTypography>
      <Grid container spacing={2}>
        <CombatRow label="Tỉ lệ chí mạng" value={`${combat.critRate}%`} />
        <CombatRow label="Sát thương chí mạng" value={`${combat.critDamage}%`} />
        <CombatRow label="Né đòn" value={`${combat.skip}%`} />
        <CombatRow label="Chính xác" value={`${combat.accuracy}%`} />
        <CombatRow
          label="Tỉ lệ trúng khi tự đánh mình"
          value={`${combat.hitRateMirror}%`}
          hint="chính xác − né của chính pet này"
        />
        <CombatRow
          label="Giảm sát thương từ thủ"
          value={`${combat.mitigation}%`}
          hint={`K = ${formatNumber(combat.defenseK)}, trần ${combat.mitigationMax}%`}
        />
      </Grid>

      {(gemSets.length > 0 || result.resonance > 0) && (
        <>
          <Divider sx={{ my: 2.5 }} />
          <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
            Bộ ngọc đang kích hoạt
          </MDTypography>
          <MDBox display="flex" gap={0.5} flexWrap="wrap">
            {gemSets.map((set) => (
              <Chip
                key={set.setId}
                size="small"
                color={set.isYinYang ? "success" : "info"}
                variant={set.isYinYang ? "filled" : "outlined"}
                label={`${set.name} ${set.tierText} (${set.value}%) — ${set.onItem}`}
              />
            ))}
            {result.resonance > 0 && (
              <Chip
                size="small"
                color="warning"
                label={`Cộng hưởng 15 viên: +${result.resonance}% chỉ số hệ ngọc`}
              />
            )}
          </MDBox>
        </>
      )}

      {hiddenStats.length > 0 && (
        <>
          <Divider sx={{ my: 2.5 }} />
          <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
            Bộ trang bị ẩn
          </MDTypography>
          {hiddenStats.map((hidden) => (
            <MDTypography variant="caption" display="block" key={hidden.id}>
              {hidden.comment || `Bộ ẩn #${hidden.id}`}
            </MDTypography>
          ))}
          <MDTypography variant="caption" color="text" display="block" mt={0.5}>
            Bộ ẩn nạp thành buff khi vào trận, nên không nằm trong bảng trên.
          </MDTypography>
        </>
      )}
    </MDBox>
  );
}

ResultCard.defaultProps = { result: null, loading: false, error: null };

ResultCard.propTypes = {
  result: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default ResultCard;
